import { CallIntelTypes } from '@frontend/api-call-intel';
import { InferencesChartData, OpportunitiesFormattedData, SchedulingFormattedData } from './types';

type LocationsMap = Record<string, string>;

type InferenceLocationValue = { inferences: Record<string, number> } | Record<string, number>;

/**
 * Formats inference-based chart data (sentiments, categories, appointment types)
 * by resolving location IDs to names and pivoting the data structure.
 */
export function formatInferencesChartData(
  locations: LocationsMap,
  data: Record<string, InferenceLocationValue> | undefined,
  prefillHelper: string[]
): InferencesChartData {
  return Object.entries(data ?? {}).reduce<InferencesChartData>((acc, [locationId, value]) => {
    const inferenceSource = value && 'inferences' in value ? value.inferences : value;
    acc[locations[locationId] || locationId] = prefillHelper.reduce<Record<string, number>>((innerAcc, inference) => {
      innerAcc[inference] = (inferenceSource as Record<string, number>)?.[inference] ?? 0;
      return innerAcc;
    }, {});
    return acc;
  }, {});
}

/**
 * Formats opportunity data by location, computing scheduled/unscheduled breakdowns
 * and leaderboard entries with total analysed calls.
 */
export function formatOpportunitiesChartData(
  locations: LocationsMap,
  data: CallIntelTypes.CallsViewByLocation['opportunitiesByLocation'] | undefined,
  sentimentsByLocation: InferencesChartData
): OpportunitiesFormattedData {
  const result: OpportunitiesFormattedData = { opportunities: {}, leaderboard: {} };

  for (const [locationId, entry] of Object.entries(data ?? {})) {
    const { unscheduledOpportunities = 0, totalOpportunities = 0 } = entry;
    const locationName = locations[locationId] || locationId;
    const totalAnalysedCalls = Object.values(sentimentsByLocation[locationName] || {}).reduce(
      (sum, value) => sum + (value ?? 0),
      0
    );
    const scheduledOpportunities = totalOpportunities - unscheduledOpportunities;

    result.opportunities[locationName] = {
      scheduledOpportunities,
      unscheduledOpportunities,
      noOpportunities: totalAnalysedCalls - totalOpportunities,
    };
    result.leaderboard[locationId] = {
      scheduledOpportunities,
      unscheduledOpportunities,
      noOpportunities: totalAnalysedCalls - totalOpportunities,
      totalAnalysedCalls,
      totalOpportunities,
    };
  }

  return result;
}

/**
 * Formats scheduling stats by location, computing rates and breakdowns
 * for both the leaderboard and stacked chart views.
 */
export function formatSchedulingChartData(
  locations: LocationsMap,
  data: CallIntelTypes.CallsViewByLocation['schedulingStatsByLocation'] | undefined
): SchedulingFormattedData {
  const result: SchedulingFormattedData = { leaderboard: {}, opportunities: {} };

  for (const [locationId, stats] of Object.entries(data ?? {})) {
    const locationName = locations[locationId] || locationId;
    const {
      scheduledOnCallCount = 0,
      scheduledOpportunitiesCount = 0,
      scheduledPostCallCount = 0,
      stillUnscheduledCount = 0,
      totalCallsAnalyzed = 0,
    } = stats;
    const unscheduledOnCall = Math.max(scheduledOpportunitiesCount - scheduledOnCallCount, 0);
    const scheduledRate =
      scheduledOpportunitiesCount > 0 ? (scheduledOnCallCount / scheduledOpportunitiesCount) * 100 : 0;

    result.leaderboard[locationId] = {
      scheduledRate,
      totalCallsAnalyzed,
      scheduledOnCallCount,
      unscheduledOnCall,
      scheduledOpportunitiesCount,
      stillUnscheduledCount,
      scheduledPostCallCount,
    };
    result.opportunities[locationName] = {
      scheduledOnCallCount,
      unscheduledOnCall,
      scheduledPostCallCount,
    };
  }

  return result;
}

/**
 * Extracts and deduplicates all inference keys from location-based data,
 * returning them in sorted order for consistent chart axis rendering.
 */
export function getAllInferencesList(data: Record<string, InferenceLocationValue> | undefined): string[] {
  const allKeys = new Set<string>();
  for (const value of Object.values(data || {})) {
    const inferenceSource = value && 'inferences' in value ? value.inferences : value;
    for (const key of Object.keys(inferenceSource || {})) {
      allKeys.add(key);
    }
  }
  return Array.from(allKeys).sort();
}
